/*
 * HomeCrest PDP pilot preview — v2.4 adapter
 * ===========================================================================
 * REVIEW TOOL. NOT A PRODUCTION FEATURE. NOT A PUBLISHING MECHANISM.
 *
 * Renders proposed pilot content over the live PDP so the complete product
 * page can be reviewed before any catalogue data is changed.
 *
 * WHY THIS EXISTS
 * ---------------
 * `product.title` is a Shopify PRODUCT RECORD field, and so are the
 * `homecrest.*` metafields the PDP reads. Shopify has no per-theme override for
 * either, so editing them to preview new copy would change the LIVE store at
 * the same instant. The preview happens in the browser instead. This file:
 *   - writes nothing to Shopify, and holds no credential to do so
 *   - is served only when `settings.fixture_enable` is on
 *   - does nothing at all unless the URL carries ?hc_pilot=1
 *   - NEVER touches price, inventory, variant or add-to-cart state
 *
 * It is a client-side overlay applied after render, so a crawler still sees the
 * CURRENT title and description. It previews the editorial and visual
 * experience. It is NOT an SEO test.
 *
 * CANONICAL ARCHITECTURE — FROZEN
 * -------------------------------
 * Six panels. This is the reference structure and is not an experiment:
 *
 *   1. Overview             theme's lede + key features + configuration cards
 *   2. Dimensions           physical measurements, size info, variant tables
 *   3. Materials & Care     materials, finish chips, construction, care
 *   4. Delivery & Assembly  verified assembly only; omitted when nothing exists
 *   5. Reviews              the theme's own system — never rebuilt, only moved
 *   6. FAQ                  4-5 questions
 *
 * There is NO Specifications panel. Every specification fact is routed to the
 * panel it belongs to: configuration to Overview, measurements to Dimensions,
 * construction and hardware to Materials & Care, assembly to Delivery.
 *
 * The page is longer than a merged layout and that is intended. Height is not
 * the objective; a premium furniture PDP with every verified fact in one clear,
 * logical home is.
 *
 * Missing data omits the block silently. No empty headings, no "data
 * unavailable", no support referrals, no internal QA language on a customer
 * surface.
 *
 * OPTION DISPLAY VALUES are applied here from a PROPOSAL registry so the
 * normalisation can be judged on the rendered page. Nothing is written to
 * Shopify; the variant's real identity is untouched and each relabelled control
 * keeps its source value in a title attribute.
 */

const PARAM = 'hc_pilot';
const SCHEMA = 'homecrest.content-schema/2.4';

function pilotRequested() {
  try {
    return new URLSearchParams(window.location.search).get(PARAM) === '1';
  } catch {
    return false;
  }
}

const qs = (s, r = document) => r.querySelector(s);
const qsa = (s, r = document) => [...r.querySelectorAll(s)];

function currentHandle() {
  const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
  return m ? m[1] : null;
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

/* ==========================================================================
   Preview-only styling
   Scoped to elements this overlay creates. Colours are inherited rather than
   declared so the chips and sub-headings sit inside the theme's own palette.
   ========================================================================== */

function injectStyle() {
  if (qs('#hc-pilot-style')) return;
  const s = document.createElement('style');
  s.id = 'hc-pilot-style';
  s.textContent = `
    .hc-pilot-sub{font-size:.72rem;letter-spacing:.12em;text-transform:uppercase;
      opacity:.62;margin:0 0 .45rem;font-weight:600;}
    .hc-pilot-block + .hc-pilot-block{margin-top:1.35rem;}
    .hc-pilot-chips{display:flex;flex-wrap:wrap;gap:6px;margin:.15rem 0 0;padding:0;list-style:none;}
    .hc-pilot-chip{font-size:.78rem;line-height:1.2;padding:5px 10px;border:1px solid currentColor;
      border-radius:2px;opacity:.78;white-space:nowrap;}
    .hc-pilot-care p{margin:0;}
    .hc-pilot-care p + p{margin-top:.5rem;}
    .hc-pilot-care .hc-pilot-care-specific{opacity:.88;}
    .hc-pilot-opener{margin:0 0 1rem;}
    .hc-pilot-opener .hc-pilot-sub{margin-bottom:.3rem;}
    .hc-pilot-opener p{margin:0;max-width:52ch;}
    .hc-pilot-family{font-size:.72rem;opacity:.5;margin:.6rem 0 .3rem;letter-spacing:.04em;}
    .hc-pilot-family:first-child{margin-top:0;}
    .hc-pilot-vtable thead th{font-size:.7rem;letter-spacing:.08em;text-transform:uppercase;opacity:.55;font-weight:600;}
    [data-hc-pilot-axes]{margin:.75rem 0 0;padding-left:1.1rem;display:flex;
      flex-direction:column;gap:.35rem;}
  `;
  document.head.appendChild(s);
}

/* ==========================================================================
   Panel ownership
   ========================================================================== */

function paneHost() {
  return qs('.hc-details__panes');
}

function tabList() {
  return qs('.hc-details__tablist');
}

/* Canonical tab order. The 360 rail is not in this list because it is not a
   tab - it is a sibling of the panes and stays visible throughout. */
const TAB_ORDER = ['overview', 'dimensions', 'materials', 'delivery', 'reviews', 'qa'];

/**
 * Returns the pane for a canonical tab, creating the tab and pane when the
 * theme did not render them.
 *
 * The theme builds a tab only when the backing metafield holds data, and most
 * pilot products have none - so Dimensions and Q&A frequently do not exist
 * server-side even though the verified v2 record has content for them. The
 * overlay therefore has to be able to add a tab, in canonical position, rather
 * than assuming one is already there.
 */
function ensurePane(key, label) {
  const host = paneHost();
  const list = tabList();
  if (!host || !list) return null;

  let pane = qs(`[data-hc-pane="${key}"]`, host);
  if (pane) {
    pane.setAttribute('data-hc-pilot-applied', key);
    return pane;
  }

  const uid = `hcpilot-${key}`;
  pane = document.createElement('div');
  pane.className = 'hc-details__pane';
  pane.setAttribute('role', 'tabpanel');
  pane.id = `hcp-${uid}`;
  pane.setAttribute('aria-labelledby', `hct-${uid}`);
  pane.setAttribute('data-hc-pane', key);
  pane.setAttribute('data-hc-pilot-applied', key);
  pane.setAttribute('data-hc-pilot-created', '');
  pane.hidden = true;

  const tab = document.createElement('button');
  tab.type = 'button';
  tab.className = 'hc-details__tab';
  tab.setAttribute('role', 'tab');
  tab.id = `hct-${uid}`;
  tab.setAttribute('aria-controls', pane.id);
  tab.setAttribute('aria-selected', 'false');
  tab.tabIndex = -1;
  tab.textContent = label;

  /* Insert both in canonical order rather than appending, so a tab added by
     the overlay does not jump to the end of the strip. */
  const idx = TAB_ORDER.indexOf(key);
  const afterTab = [...list.querySelectorAll('[role="tab"]')].find((t) => {
    const k = qs(`#${CSS.escape(t.getAttribute('aria-controls'))}`)?.getAttribute('data-hc-pane');
    return k && TAB_ORDER.indexOf(k) > idx;
  });
  if (afterTab) list.insertBefore(tab, afterTab);
  else list.appendChild(tab);

  const afterPane = [...host.querySelectorAll('[data-hc-pane]')]
    .find((p) => TAB_ORDER.indexOf(p.getAttribute('data-hc-pane')) > idx);
  if (afterPane) host.insertBefore(pane, afterPane);
  else host.appendChild(pane);

  return pane;
}

/** Empties a pane's body, keeping it in place in the tab strip. */
function fillPane(key, label, html) {
  const pane = ensurePane(key, label);
  if (!pane) return null;
  pane.innerHTML = html;
  return pane;
}

function table(rows) {
  return `<table class="hc-details__table">${rows
    .map(([l, v]) => `<tr><th scope="row">${esc(l)}</th><td>${esc(v)}</td></tr>`)
    .join('')}</table>`;
}

function block(label, inner) {
  return `<div class="hc-pilot-block"><p class="hc-pilot-sub">${esc(label)}</p>${inner}</div>`;
}

/* ==========================================================================
   Banner
   ========================================================================== */

function banner(product, verdict) {
  const blocked = verdict?.status === 'BLOCKED';
  const el = document.createElement('div');
  el.setAttribute('data-hc-pilot-banner', '');
  el.setAttribute('data-hc-pilot-status', verdict?.status || 'UNKNOWN');
  el.style.cssText = [
    'position:fixed', 'top:0', 'left:0', 'right:0', 'z-index:99999',
    `background:${blocked ? '#7a1f1f' : '#1F1B17'}`, 'color:#F5F1EA',
    'font:500 12px/1.4 Inter,system-ui,sans-serif', 'letter-spacing:.06em',
    'text-transform:uppercase', 'padding:8px 16px', 'text-align:center',
  ].join(';');
  el.textContent = blocked
    ? `Pilot v2.4 · ${product.new_name} · NOT PUBLISHABLE — ${verdict.missing_decisive.join(', ')}`
    : `Pilot v2.4 · ${product.new_name} · proposed content, nothing saved to Shopify`;
  document.body.appendChild(el);
  document.documentElement.style.scrollPaddingTop = '40px';
}

/* ==========================================================================
   Name propagation
   ========================================================================== */

function applyNameEverywhere(oldTitle, p) {
  if (!oldTitle) return 0;
  const oldToken = oldTitle.split(/[\s—–-]+/)[0];
  const sels = [
    '.hc-pdp-crumbs__current', '.sticky-add-to-cart__title', '.hc-cust__title',
    '.visually-hidden', '.view-product-title', '.resource-card__title',
  ];
  let n = 0;
  for (const sel of sels) {
    for (const el of qsa(sel)) {
      const t = el.textContent;
      if (t.includes(oldTitle)) { el.textContent = t.replace(oldTitle, p.new_title); n += 1; }
      else if (oldToken.length > 3 && t.includes(oldToken)) {
        el.textContent = t.replace(oldToken, p.new_name); n += 1;
      }
    }
  }
  return n;
}

/* ==========================================================================
   Containers the PDP only builds when a metafield holds data
   ========================================================================== */

function ensureIntro() {
  let el = qs('.hc-pdp-intro__text');
  if (el) return el;
  const anchor = qs('.hc-pdp-value') || qs('[data-homecrest-action]')?.closest('div') || qs('.hc-pdp-editorial');
  if (!anchor) return null;
  const wrap = document.createElement('div');
  wrap.className = 'hc-pdp-intro';
  wrap.setAttribute('data-hc-pilot-created', '');
  el = document.createElement('div');
  el.className = 'hc-pdp-intro__text';
  wrap.appendChild(el);
  anchor.parentNode.insertBefore(wrap, anchor);
  return el;
}

/**
 * The Overview pane's prose and highlights containers.
 *
 * The theme only renders an Overview tab when homecrest.overview, highlights
 * or spec_rows hold data. Vihan has none of the three, so without this the
 * product lost its Overview tab entirely while the verified v2 record had a
 * full two-paragraph overview and six key features. Build the containers
 * inside the pane - never outside it, or the content lands beside the tab
 * strip instead of in it.
 */
function ensureOverview() {
  const pane = ensurePane('overview', 'Overview');
  if (!pane) return { prose: null, list: null, glance: null };

  /* Mirror the theme's own Overview structure - a left column carrying the
     prose and the key features, and an At a Glance column beside it. When the
     theme rendered the pane these already exist and are simply found; when the
     overlay had to create the pane (a product whose metafields gave the theme
     nothing to render) they are built to the SAME shape, so preview and
     production are the same component either way. */
  pane.classList.add('hc-details__pane--overview');

  let main = qs('.hc-details__overview-main', pane);
  if (!main) {
    main = document.createElement('div');
    main.className = 'hc-details__overview-main';
    main.setAttribute('data-hc-pilot-created', '');
    pane.prepend(main);
  }

  let prose = qs('.hc-details__prose', pane);
  if (!prose) {
    prose = document.createElement('div');
    prose.className = 'hc-details__prose rte';
    prose.setAttribute('data-hc-pilot-created', '');
    main.appendChild(prose);
  }

  let list = qs('.hc-details__highlight-list', pane);
  if (!list) {
    const wrap = document.createElement('div');
    wrap.className = 'hc-details__block';
    wrap.setAttribute('data-hc-pilot-created', '');
    wrap.innerHTML = '<h3 class="hc-details__sub">Key features</h3>'
      + '<ul class="hc-details__highlight-list"></ul>';
    main.appendChild(wrap);
    list = qs('.hc-details__highlight-list', wrap);
  }

  let glance = qs('[data-hc-glance]', pane);
  if (!glance) {
    glance = document.createElement('div');
    glance.className = 'hc-details__glance';
    glance.setAttribute('data-hc-glance', '');
    glance.setAttribute('data-hc-pilot-created', '');
    glance.hidden = true;
    glance.innerHTML = '<h3 class="hc-details__sub">At a glance</h3>'
      + '<table class="hc-details__table hc-details__table--glance"></table>';
    pane.appendChild(glance);
  }

  return { prose, list, glance };
}

/* ==========================================================================
   Apply
   ========================================================================== */

function applyTitle(p) {
  const h1 = qs('.hc-pdp-editorial h1') || qs('main h1') || qs('h1');
  const oldTitle = h1 ? h1.textContent.trim() : null;
  if (h1) { h1.textContent = p.new_title; h1.setAttribute('data-hc-pilot-applied', 'title'); }

  document.title = p.seo_title;
  let meta = qs('meta[name="description"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'description');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', p.meta_description);
  for (const sel of ['meta[property="og:title"]', 'meta[name="twitter:title"]']) {
    qs(sel)?.setAttribute('content', p.seo_title);
  }
  for (const sel of ['meta[property="og:description"]', 'meta[name="twitter:description"]']) {
    qs(sel)?.setAttribute('content', p.meta_description);
  }
  return oldTitle;
}

/* ==========================================================================
   Highlight strip
   The five-item band between the hero and the tab strip.

   The strip is a THEME component: its markup, classes, spacing, dividers and
   icons all live in snippets/homecrest-highlight-strip.liquid and
   homecrest-pdp-details.css. This function does one thing - it fills the
   mount the theme already emitted with the five items from the verified v2
   record. It authors no design and it invents no content: a product whose
   record has no `highlights` gets no band, and the mount stays hidden.

   The icons are CLONED from the theme's own rendered set rather than
   re-drawn here, so the overlay and the server-rendered strip can never show
   two different versions of the same mark.
   ========================================================================== */

function highlightIcon(name) {
  const source = qs('[data-hc-highlight-icons]');
  const node = source?.querySelector(`[data-icon="${CSS.escape(String(name || ''))}"] svg`);
  return node ? node.cloneNode(true).outerHTML : '';
}

function applyHighlights(p) {
  const mount = qs('[data-hc-highlights]');
  if (!mount) return 0;

  const items = (p.highlights || []).filter((h) => h && h.title);
  if (!items.length) return 0;

  const list = document.createElement('ul');
  list.className = 'hc-highlights__list';
  list.setAttribute('aria-label', 'Product highlights');
  list.innerHTML = items
    .map(
      (h) => `<li class="hc-highlights__item">
          <span class="hc-highlights__icon">${highlightIcon(h.icon)}</span>
          <h3 class="hc-highlights__title">${esc(h.title)}</h3>
          ${h.description ? `<p class="hc-highlights__text">${esc(h.description)}</p>` : ''}
        </li>`
    )
    .join('');

  /* Replace the list only. The icon source stays in the mount so a re-run
     still has something to clone from. */
  qs('.hc-highlights__list', mount)?.remove();
  mount.appendChild(list);
  mount.removeAttribute('hidden');
  mount.setAttribute('data-hc-pilot-applied', 'highlights');
  return items.length;
}

function applyIntro(p) {
  const intro = ensureIntro();
  if (intro) {
    intro.innerHTML = `<p>${esc(p.short_intro)}</p>`;
    intro.setAttribute('data-hc-pilot-applied', 'intro');
  }
}

function applyOverview(p) {
  const { prose, list, glance } = ensureOverview();
  if (prose) {
    prose.innerHTML = p.overview.split('\n\n').map((x) => `<p>${esc(x)}</p>`).join('');
    prose.setAttribute('data-hc-pilot-applied', 'overview');
  }
  if (list && p.key_features?.length) {
    list.innerHTML = p.key_features.map((f) => `<li class="hc-details__highlight">${esc(f)}</li>`).join('');
    list.setAttribute('data-hc-pilot-applied', 'features');
  }

  /* Configuration and use-case facts - seating, storage type, shape, drawer
     and door counts, headboard - are AT A GLANCE facts, and At a Glance is a
     THEME component: its markup, its two-column table and its heading live in
     sections/homecrest-product-details.liquid. This fills the mount the theme
     already emitted rather than authoring a second presentation, so the
     previewed column and a server-rendered one are the same component.
     Measurements are deliberately absent here - those belong to Dimensions. */
  const cards = p.overview_specs || [];
  if (glance && cards.length) {
    const table = qs('table', glance);
    if (table) {
      table.innerHTML = cards
        .map((c) => `<tr><th scope="row">${esc(c.label)}</th><td>${esc(c.value)}</td></tr>`)
        .join('');
      glance.removeAttribute('hidden');
      glance.removeAttribute('data-hc-glance-mount');
      glance.setAttribute('data-hc-pilot-applied', 'glance');
    }
  }
  return cards.length;
}

const AXIS_LABELS = {
  length: 'Length', width: 'Width', depth: 'Depth', height: 'Height',
  seat_height: 'Seat height', seat_depth: 'Seat depth',
  back_height: 'Back height', arm_height: 'Arm height',
  bed_length: 'Bed length', bed_width: 'Bed width', diameter: 'Diameter',
};

const MATERIAL_LABELS = {
  primary: 'Primary material', frame: 'Frame', framework: 'Framework', legs: 'Legs',
  carcass: 'Carcass', doors: 'Doors', cabinet: 'Cabinet', detail: 'Detail',
  fill: 'Fill', seating: 'Seating', upholstery: 'Upholstery',
  cushioning: 'Cushioning', seat_cushions: 'Seat cushions', back_cushions: 'Back cushions',
};

/** "159 cm / 62.5 in" — both units, centimetres first, per the type manifest. */
function pair(v) {
  if (v.in_values && v.cm_values) {
    return v.cm_values.map((cm, i) => `${cm} cm / ${v.in_values[i]} in`).join(' · ');
  }
  return `${v.cm} cm / ${v.in} in`;
}

/**
 * Panel 2 — Dimensions & Specifications.
 *
 * One panel so the page is not carrying two adjacent label/value tables under
 * separate headings, but two labelled blocks inside it so a buyer scanning for
 * a measurement is not reading past configuration to find it.
 *
 * Either block is dropped when empty; the panel itself is dropped when both are.
 */
/**
 * Panel 2 — Dimensions.
 *
 * Physical measurements and size information only. Configuration facts live
 * under Overview, materials under Materials & Care. Nothing is hidden here to
 * shorten the page: every labelled axis, every unlabelled source figure, and
 * every per-variant table renders.
 */
function applyDimensions(p) {
  const rows = Object.entries(p.dimensions || {}).map(([k, v]) => [AXIS_LABELS[k] || k, pair(v)]);
  for (const r of p.dimension_specs || []) rows.push([r.label, r.value]);
  for (const u of p.unlabelled_dimensions || []) {
    rows.push(['A further figure', `${u.value} ${u.unit} — the maker publishes this without naming the axis`]);
  }
  const tables = p.variant_tables || [];
  if (!rows.length && !tables.length) return null;

  let html = rows.length ? table(rows) : '';
  for (const t of tables) html += block(t.label, variantTable(t));
  if (!fillPane('dimensions', 'Dimensions', html)) return null;
  return { rows: rows.length, tables: tables.length };
}

/**
 * A fact that genuinely varies by variant, as a real table.
 *
 * Arin's cushion counts were one 121px mobile cell holding five cases in
 * prose. Used ONLY where the value actually differs between variants - the
 * verifier fails a table whose rows all say the same thing, because that is a
 * spec row wearing a table's clothes.
 */
function variantTable(t) {
  return `<table class="hc-details__table hc-pilot-vtable">` +
    `<thead><tr><th scope="col">${esc(t.axis_label || 'Variant')}</th>` +
    `<th scope="col">${esc(t.label)}</th></tr></thead><tbody>` +
    t.rows.map((r) => `<tr><th scope="row">${esc(r.variant)}</th><td>${esc(r.value)}</td></tr>`).join('') +
    `</tbody></table>`;
}

/**
 * Panel 3 — Materials & Care.
 *
 * Care follows directly from the materials above it, so the two read as one
 * thought rather than two panels.
 *
 * Finish renders as chips, not a comma-joined sentence. At four values the
 * sentence was a 101px cell; at Tanvi's twenty it would be unreadable. The chip
 * labels are the live Shopify option values verbatim - this is a presentation
 * change only, and normalising the option values themselves is a separate task.
 */
/**
 * Groups a long finish list by its leading family word.
 *
 * Tanvi sells twenty finishes in three families the maker already uses -
 * Antique, Country, and plain. Twenty chips in one wrap is a wall; three short
 * runs is a list. This is a GROUPING, not a rename: every chip still carries
 * its full value, because six of Tanvi's twenty are Antique variants of a plain
 * finish that also exists, and shortening them would collapse six distinct
 * purchasable variants onto six others.
 */
function groupFinishes(values) {
  if (values.length <= 8) return [{ family: null, values }];
  const counts = {};
  for (const v of values) {
    const first = v.split(' ')[0];
    counts[first] = (counts[first] || 0) + 1;
  }
  const families = Object.keys(counts).filter((k) => counts[k] >= 2);
  if (!families.length) return [{ family: null, values }];
  const groups = [];
  const used = new Set();
  for (const fam of families) {
    const vals = values.filter((v) => v.startsWith(`${fam} `));
    if (vals.length < 2) continue;
    vals.forEach((v) => used.add(v));
    groups.push({ family: fam, values: vals });
  }
  const rest = values.filter((v) => !used.has(v));
  if (rest.length) groups.push({ family: null, values: rest });
  return groups;
}

function applyMaterialsAndCare(p, display) {
  const matRows = Object.entries(p.materials || {}).map(([k, v]) => [MATERIAL_LABELS[k] || k, v]);
  // Construction, technique, hardware and provenance sit with the materials.
  for (const r of p.material_specs || []) matRows.push([r.label, r.value]);
  const finishes = p.finish || [];
  const care = p.care;
  const careShared = typeof care === 'string' ? care : care?.shared;
  const careSpecific = typeof care === 'string' ? null : care?.specific;
  if (!matRows.length && !finishes.length && !careShared) return null;

  let html = '';
  if (matRows.length) html += block('Materials', table(matRows));
  if (finishes.length) {
    const groups = groupFinishes(finishes);
    const chipList = (vals) => `<ul class="hc-pilot-chips" data-hc-pilot-finish>${vals
      .map((f) => `<li class="hc-pilot-chip" title="${esc(f)}">${esc(display(f))}</li>`).join('')}</ul>`;
    let inner = '';
    if (groups.length === 1 && !groups[0].family) inner = chipList(groups[0].values);
    else {
      inner = groups.map((g) => g.family
        ? `<p class="hc-pilot-family">${esc(g.family)}</p>${chipList(g.values)}`
        : chipList(g.values)).join('');
    }
    html += block(`Finish · ${finishes.length}`, inner);
  } else if (p.finish_display_note) {
    html += block('Finish', `<p>${esc(p.finish_display_note)}</p>`);
  }
  if (careShared) {
    html += block('Care', `<div class="hc-pilot-care"><p>${esc(careShared)}</p>` +
      (careSpecific ? `<p class="hc-pilot-care-specific">${esc(careSpecific)}</p>` : '') + '</div>');
  }
  if (!fillPane('materials', 'Materials & Care', html)) return null;
  return { materials: matRows.length, finishes: finishes.length, care: !!careShared };
}

/**
 * Panel 4 — Delivery & Assembly. A dedicated section in the canonical PDP.
 *
 * Renders only what the maker actually states. Delivery windows and warranty
 * remain UNCONFIRMED brand policy: they are never invented, never referred to
 * support, and never filled with an apology. Where a product has nothing
 * verified, the panel is omitted rather than shown empty - which is the
 * graceful handling, not a shortening tactic.
 */
function applyDeliveryAssembly(p) {
  const rows = (p.delivery_specs || []).map((r) => [r.label, r.value]);
  if (p.delivery?.status === 'CONFIRMED' && p.delivery.days_min) {
    rows.push(['Delivery', `${p.delivery.days_min}–${p.delivery.days_max} working days`]);
  } else {
    /* No product-level delivery window in the verified record. The theme falls
       back to the approved COMPANY policy in that case, and this overlay must
       show the same thing or the preview would tell a reviewer that delivery is
       missing when the real page states it. The wording is read off the section
       the theme already rendered it into - not duplicated here. */
    const host = qs('[data-hc-delivery-policy]');
    const policy = host?.getAttribute('data-hc-delivery-policy');
    if (policy) rows.push([host.getAttribute('data-hc-delivery-label') || 'Estimated delivery', policy]);
  }
  if (p.warranty?.status === 'CONFIRMED' && p.warranty.years) {
    rows.push(['Warranty', `${p.warranty.years} years`]);
  }
  if (!rows.length) return null;
  if (!fillPane('delivery', 'Delivery & Assembly', table(rows))) return null;
  return rows.length;
}

/**
 * Relabels the variant picker with the proposed display values.
 *
 * PRESENTATION ONLY. The input value, the variant id and everything Shopify
 * uses to identify the purchasable variant are untouched - only the visible
 * text of the label changes, and the source value is preserved in a title
 * attribute so it remains traceable on the page itself.
 *
 * This exists so the normalisation can be judged on the rendered page before a
 * decision is taken to write it to Shopify.
 */
function applyOptionDisplay(displayMap) {
  if (!displayMap.size) return 0;
  let n = 0;
  for (const input of qsa('variant-picker input, .hc-cust__value input')) {
    const src = input.value;
    if (!displayMap.has(src)) continue;
    const label = input.closest('label') || qs(`label[for="${input.id}"]`);
    if (!label) continue;
    const dsp = displayMap.get(src);

    /* The theme does not always print the source value verbatim - Horizon
       renders "Medium Teak Wood Finish" as "Medium Teak Wood", dropping the
       last word. So match on a prefix relationship rather than equality, or
       four of Neel's four labels are silently missed. */
    const matches = (t) => {
      const a = t.trim().toLowerCase();
      const b = src.trim().toLowerCase();
      return a.length > 2 && (a === b || b.startsWith(a) || a.startsWith(b));
    };
    for (const node of [...label.childNodes]) {
      if (node.nodeType === 3 && matches(node.nodeValue)) { node.nodeValue = dsp; n += 1; }
    }
    for (const el of qsa('span, .variant-option__label, .hc-cust__value-text', label)) {
      if (el.children.length === 0 && matches(el.textContent)) { el.textContent = dsp; n += 1; }
    }
    if (input.hasAttribute('aria-label') && matches(input.getAttribute('aria-label'))) {
      input.setAttribute('aria-label', dsp);
    }
    label.setAttribute('title', src);
    label.setAttribute('data-hc-pilot-source-value', src);
  }
  // Selected-variant summaries print the value as text too.
  for (const el of qsa('.hc-cust__current, [data-hc-cust-summary], .variant-option__selected-value')) {
    let t = el.textContent;
    for (const [src, dsp] of displayMap) if (t.includes(src)) { t = t.split(src).join(dsp); n += 1; }
    if (t !== el.textContent) el.textContent = t;
  }
  return n;
}

function applyFaq(p) {
  if (!p.faq?.length) return null;
  const html = `<div class="hc-details__faq-list" data-hc-pilot-faq>` + p.faq
    .map((f) => `<details class="hc-details__faq-row"><summary class="hc-details__faq-q">${esc(f.q)}<span class="hc-details__chev" aria-hidden="true"></span></summary><div class="hc-details__faq-a rte"><p>${esc(f.a)}</p></div></details>`)
    .join('') + `</div>`;
  if (!fillPane('qa', 'Q&A', html)) return null;
  return p.faq.length;
}

/**
 * Customise.
 *
 * The opener is the product-specific sentence and it now renders on the page,
 * above the Customise control, rather than only inside the drawer. Phase 5
 * found the strongest new content in v2 was also the least visible: the axes
 * only appeared after opening the drawer and the opener never rendered at all,
 * because the previous selectors matched nothing.
 *
 * The axes stay inside the drawer. The copy must not imply that any request is
 * guaranteed manufacturable, and must not restrict what a customer may ask for.
 */
function applyCustomise(p) {
  if (!p.customise) return { opener: false, axes: 0 };
  let openerPlaced = false;

  const trigger = qsa('[data-homecrest-action="customise"]')[0]
    || qsa('button, a').find((e) => /customise|customize/i.test(e.textContent || ''));
  const anchor = qs('.hc-pdp-actions') || trigger?.closest('.hc-pdp-actions') || trigger?.parentElement;

  /* The section head is a THEME component - eyebrow, type, spacing and
     centring all live in blocks/homecrest-pdp-actions.liquid and
     homecrest-pdp-hero.css. The overlay only fills the mount the theme
     already emitted, so the previewed head and a head rendered from the
     product's own metafield are the same component, not two designs. */
  const head = qs('[data-hc-makeyours-head]');
  const question = head && qs('[data-hc-makeyours-question]', head);

  if (head && question && p.customise.opener) {
    question.textContent = p.customise.opener;
    head.removeAttribute('hidden');
    head.removeAttribute('data-hc-makeyours-mount');
    head.setAttribute('data-hc-pilot-applied', 'customise-opener');
    openerPlaced = true;
  } else if (anchor && p.customise.opener && !qs('[data-hc-pilot-opener]')) {
    /* Fallback for a theme build that predates the mount. */
    const box = document.createElement('div');
    box.className = 'hc-pilot-opener';
    box.setAttribute('data-hc-pilot-opener', '');
    box.setAttribute('data-hc-pilot-applied', 'customise-opener');
    box.innerHTML = `<p class="hc-pilot-sub">Make it yours</p><p>${esc(p.customise.opener)}</p>`;
    anchor.parentNode.insertBefore(box, anchor);
    openerPlaced = true;
  }

  let axes = 0;
  const drawerBody = qs('.hc-cust__body') || qs('.hc-pdp-customise');
  if (drawerBody && p.customise.axes?.length && !qs('[data-hc-pilot-axes]')) {
    const ul = document.createElement('ul');
    ul.setAttribute('data-hc-pilot-axes', '');
    ul.innerHTML = p.customise.axes.map((a) => `<li>${esc(a)}</li>`).join('');
    drawerBody.appendChild(ul);
    axes = p.customise.axes.length;
  }
  return { opener: openerPlaced, axes };
}

/** Collapses a Shopify CDN URL to the underlying file. */
function normSrc(url) {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/_\d+x\d*(?=\.[a-z]+$)/i, '');
  } catch { return url; }
}

/**
 * One ALT per image, applied to every node that renders that image.
 *
 * The PDP renders each photograph in five places: the main slideshow, its
 * thumbnail strip, the zoom dialog, the zoom dialog's thumbnails, and the
 * sticky bar. A 15-image product is ~61 ALT-bearing nodes. Related-product and
 * predictive-search cards are excluded - they show OTHER products.
 */
function applyAlt(p) {
  const imgs = qsa([
    '.hc-pdp-editorial .product-media img',
    'slideshow-component img',
    'zoom-dialog img',
    '.sticky-add-to-cart__image img',
  ].join(', '));
  const alts = p.image_alt || [];
  const order = [];
  const bySrc = new Map();
  for (const img of imgs) {
    const key = normSrc(img.getAttribute('src') || img.currentSrc || img.src);
    if (!bySrc.has(key)) { bySrc.set(key, []); order.push(key); }
    bySrc.get(key).push(img);
  }
  let applied = 0;
  order.forEach((key, i) => {
    if (i >= alts.length) return;
    for (const img of bySrc.get(key)) { img.setAttribute('alt', alts[i]); applied += 1; }
  });
  return { nodes: imgs.length, uniqueImages: order.length, alts: alts.length, applied };
}

/* ==========================================================================
   Run
   ========================================================================== */

let applied = null;
let lastReport = null;

async function activate() {
  const handle = currentHandle();
  if (!handle) return null;

  const tag = qs('script[data-content][src*="homecrest-pilot-preview"]');
  const src = tag?.dataset.content || window.HomeCrestPilotContentUrl;
  const verdictSrc = tag?.dataset.verdicts || window.HomeCrestPilotVerdictsUrl;
  if (!src) { console.warn('HomeCrest pilot: no content URL configured.'); return null; }

  let doc;
  try {
    doc = await (await fetch(src)).json();
  } catch (error) {
    console.warn('HomeCrest pilot: could not load content.', error);
    return null;
  }

  /* Guard against a silent fall back to an older asset. */
  if (doc.schema !== SCHEMA) {
    console.error(
      `HomeCrest pilot: expected ${SCHEMA} but loaded "${doc.schema}". Refusing to render.`
    );
    document.documentElement.setAttribute('data-hc-pilot-error', 'wrong-schema');
    return null;
  }

  let verdicts = {};
  if (verdictSrc) {
    try { verdicts = (await (await fetch(verdictSrc)).json()).byHandle || {}; }
    catch { console.warn('HomeCrest pilot: verdicts unavailable; banner will show UNKNOWN.'); }
  }

  const product = doc.products.find((p) => p.handle === handle);
  if (!product) {
    console.info(`HomeCrest pilot: "${handle}" is not one of the ${doc.products.length} pilot products.`);
    return null;
  }
  const verdict = verdicts[handle];

  injectStyle();
  const oldTitle = applyTitle(product);
  const highlights = applyHighlights(product);
  applyIntro(product);
  applyOverview(product);

  /* Proposed customer-facing option values. PROPOSAL ONLY - applied at render
     time so the normalisation can be reviewed before anything is written to
     Shopify. The variant's real identity is never touched. */
  let normalisation = null;
  const normSrcUrl = tag?.dataset.options || window.HomeCrestPilotOptionsUrl;
  if (normSrcUrl) {
    try { normalisation = (await (await fetch(normSrcUrl)).json()).products?.[handle] || null; }
    catch { console.warn('HomeCrest pilot: option normalisation unavailable; showing source values.'); }
  }
  const displayMap = new Map((normalisation?.mappings || []).map((m) => [m.source_value, m.display_value]));
  const display = (v) => displayMap.get(v) ?? v;

  /* The theme owns the tab strip. The overlay fills the panes it has verified
     content for and creates a tab only where the theme rendered none. Reviews
     is never touched: it is the theme's own pane and its own empty state. */
  const dims = applyDimensions(product);
  const matCare = applyMaterialsAndCare(product, display);
  const delivery = applyDeliveryAssembly(product);
  const faq = applyFaq(product);
  const pickerRelabelled = applyOptionDisplay(displayMap);

  /* The overlay may have added tabs after the tablist upgraded. Ask it to
     re-assert a single-selection state so exactly one pane is visible. */
  qs('hc-detail-tabs')?.refresh?.();

  const altReport = applyAlt(product);
  const renamed = applyNameEverywhere(oldTitle, product);
  const customise = applyCustomise(product);
  banner(product, verdict);

  const panelsBuilt = qsa('[data-hc-pane]').map((e) => e.getAttribute('data-hc-pane'));

  document.documentElement.setAttribute('data-hc-pilot-version', '2.4');
  document.documentElement.setAttribute('data-hc-pilot-name', product.new_name);
  document.documentElement.setAttribute('data-hc-pilot-schema', doc.schema);

  applied = product;
  lastReport = {
    handle, name: product.new_name, verdict, altReport, renamed, customise, highlights,
    oldTitle, panelsBuilt, dims, matCare,
    deliveryRows: delivery, faqCount: faq, pickerRelabelled,
  };

  console.log(
    `%cHomeCrest pilot v2.4: ${product.new_name}%c\n` +
      `was "${oldTitle}"\n` +
      `status: ${verdict?.status || 'UNKNOWN'}` +
      (verdict?.missing_decisive?.length ? ` — ${verdict.missing_decisive.join(', ')}` : '') + '\n' +
      `highlights: ${highlights} of 5\n` +
      `tabs: ${panelsBuilt.join(', ')}\n` +
      `alt: ${altReport.alts} strings over ${altReport.uniqueImages} unique images (${altReport.applied} nodes)\n` +
      `customise opener: ${customise.opener ? 'rendered' : 'NOT PLACED'}\n` +
      'Client-side overlay only. Nothing written to Shopify. Not an SEO preview.',
    'font-weight:700;background:#1F1B17;color:#F5F1EA;padding:2px 8px', 'color:inherit'
  );
  if (altReport.alts !== altReport.uniqueImages) {
    console.warn(`HomeCrest pilot: ${altReport.alts} ALT strings for ${altReport.uniqueImages} unique images.`);
  }
  return product;
}

if (pilotRequested()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => activate(), { once: true });
  } else {
    activate();
  }
}

window.HomeCrestPilot = { PARAM, SCHEMA, activate, applied: () => applied, report: () => lastReport };
